import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { action, defineScreen } from '@/shared/screen/define';

import { RECONCILIATION_PATH, RECONCILIATION_TABS } from './reconciliation.search';

// Manifesto (U23/U42). Sem permissão de recurso: tenant raiz e CONCILIATION_MENU === 1 (router.tsx:777-786).
// As ações que dependem do formulário da tela ligam o `run` real em `reconciliation.agent.ts`.
const needsScreen = () => {
  throw new Error('This action requires the open Reconciliation screen.');
};
const civilDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const reconciliationOpenCaixa = action({
  id: 'reconciliation.caixa.open',
  kind: 'draft',
  label: msg`Enviar arquivo da Conciliação Caixa`,
  description: msg`Abre o painel de envio do arquivo da Conciliação Caixa (FGTS). Quem escolhe o arquivo e executa é a pessoa.`,
  run: ({ nav }) => nav.to(RECONCILIATION_PATH, { search: { sheet: 'caixa' } }),
});
export const reconciliationOpenSiape = action({
  id: 'reconciliation.siape.open',
  kind: 'draft',
  label: msg`Enviar arquivo da Conciliação SIAPE`,
  description: msg`Abre o painel de envio do CSV da Conciliação SIAPE, a liquidação em lote. Quem escolhe o arquivo e executa é a pessoa.`,
  run: ({ nav }) => nav.to(RECONCILIATION_PATH, { search: { tab: 'siape', sheet: 'siape' } }),
});
export const reconciliationSiapeTemplate = action({
  id: 'reconciliation.siape.template',
  kind: 'read',
  label: msg`Baixar template SIAPE`,
  description: msg`Baixa o template do CSV da Conciliação SIAPE (separado por ponto e vírgula, datas dd/MM/aaaa, reais com vírgula). Não muda nada.`,
  run: needsScreen,
});
export const liquidationFileInput = z.strictObject({
  productCategories: z.array(z.string().min(1)).min(1),
  dateMin: civilDate,
  dateMax: civilDate,
  fundId: z.string().min(1).optional(),
});
export const reconciliationGenerateFiles = action({
  id: 'reconciliation.liquidationFile.generate',
  kind: 'write',
  label: msg`Gerar relatórios de conciliação`,
  description: msg`Gera os relatórios de conciliação (arquivos de liquidação por cessionário) para as categorias de produto e o período de liquidação informados (AAAA-MM-DD), com fundId opcional para um cessionário só. Mostra os links dos relatórios na tela. Pede a confirmação da pessoa.`,
  input: liquidationFileInput,
  preview: (input) => ({
    title: i18n._(msg`Gerar relatórios de conciliação`),
    detail: i18n._(msg`Serão gerados os arquivos de liquidação de ${input.dateMin} a ${input.dateMax} para ${input.productCategories.join(', ')}.`),
  }),
  run: needsScreen,
});
export const earlyLiquidationInput = z.strictObject({
  opId: z.string().min(1),
  date: civilDate,
  liquidateByValue: z.enum(['PresentValue', 'FutureValue']).optional(),
  incluirVencidos: z.boolean().optional(),
});
export const reconciliationCalculate = action({
  id: 'reconciliation.earlyLiquidation.calculate',
  kind: 'read',
  label: msg`Calcular liquidação antecipada`,
  description: msg`Calcula a liquidação antecipada de uma operação (opId) numa data (AAAA-MM-DD), por PresentValue (valor presente) ou FutureValue (valor futuro), com ou sem as parcelas vencidas. Só calcula: não muda nada.`,
  input: earlyLiquidationInput,
  run: needsScreen,
});
export const reconciliationSettle = action({
  id: 'reconciliation.earlyLiquidation.settle',
  kind: 'destructive',
  label: msg`Baixar parcelas`,
  description: msg`Baixa as parcelas da operação calculada na tela, com os mesmos valores do formulário. Movimenta valores e pede a confirmação da pessoa. Calcule antes.`,
  input: z.strictObject({}),
  preview: () => ({
    title: i18n._(msg`Baixar parcelas`),
    detail: i18n._(msg`As parcelas da operação calculada na tela serão baixadas.`),
    tone: 'destructive',
  }),
  run: needsScreen,
});

export const reconciliationScreen = defineScreen({
  id: 'reconciliation.hub',
  path: RECONCILIATION_PATH,
  title: msg`Conciliação`,
  description: msg`Ferramentas de conciliação da UY3: conciliação do arquivo da Caixa (FGTS), liquidação em lote pelo CSV do SIAPE, relatórios de liquidação por cessionário e liquidação antecipada de uma operação.`,
  access: { rootTenancy: true, flag: { key: 'CONCILIATION_MENU', equals: 1 } },
  place: { icon: 'receipt-text', section: 'conciliation', tabs: RECONCILIATION_TABS },
  actions: [
    reconciliationOpenCaixa,
    reconciliationOpenSiape,
    reconciliationSiapeTemplate,
    reconciliationGenerateFiles,
    reconciliationCalculate,
    reconciliationSettle,
  ],
});
